import type { JSX } from 'react';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { RecodeErrorType } from './CloudConnectorRecodeEntryWithoutExitConditionsOutlet.tsx';
import { useNavigate, useParams } from 'react-router';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';

export function CloudConnectorRecodeSuccessResponse() {
    const navigate = useNavigate();
    return (
        <ConfirmationDialog
            open
            title={
                <Localized
                    de="Rabatt erfolgreich"
                    fr="Rabais réussi"
                    it="Ribasso riuscito"
                    en="Discount successful"
                />
            }
            onConfirm={() => navigate('..', { replace: true })}
            confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
            dialogVersion={ConfirmationDialogVersions.SUCCESS}
        >
            <p>
                <Localized
                    de="Der Rabatt wurde erfolgreich erfasst."
                    fr="Le rabais a été saisi avec succès."
                    it="Il ribasso è stato registrato con successo."
                    en="The discount has been successfully recorded."
                />
            </p>
        </ConfirmationDialog>
    );
}

function isRecodeErrorType(value?: string): value is RecodeErrorType {
    return Object.values(RecodeErrorType).includes(value as RecodeErrorType);
}

export function getRecodeErrorType(value?: string): RecodeErrorType | null {
    if (isRecodeErrorType(value)) {
        return value;
    }
    return null;
}

export function CloudConnectorRecodeErrorResponse(): JSX.Element {
    const urlParams = useParams<{ type: string }>();
    const type = getRecodeErrorType(urlParams.type);
    const navigate = useNavigate();
    return (
        <ConfirmationDialog
            open
            title={<RecodeResponseErrorTitle type={type} />}
            onConfirm={() => navigate('..', { replace: true })}
            confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
            dialogVersion={ConfirmationDialogVersions.ERROR}
        >
            <p>
                <CloudConnectorRecodeErrorText type={type} />
            </p>
        </ConfirmationDialog>
    );
}

export function CloudConnectorRecodeErrorText({
    type,
}: {
    type: RecodeErrorType | null;
}): JSX.Element {
    switch (type) {
        case RecodeErrorType.ALREADY_PAID:
            return (
                <Localized
                    de="Das Ticket wurde bereits bezahlt."
                    fr="Le ticket a déjà été payé."
                    it="Il ticket è già stato pagato."
                    en="The ticket has already been paid."
                />
            );
        case RecodeErrorType.IDENTIFICATION_UNKNOWN:
            return (
                <Localized
                    de="Das Ticket ist unbekannt."
                    fr="Le ticket est inconnue."
                    it="Il ticket è sconosciuto."
                    en="The ticket is unknown."
                />
            );
        case RecodeErrorType.PARSING_FAILED:
            return (
                <Localized
                    de="Der Inhalt des QR-Codes ist nicht der eines Tickets."
                    fr="Le contenu du code QR n'est pas celui d'un ticket."
                    it="Il contenuto del QR code non è quello di un ticket."
                    en="The content of the QR code is not that of a ticket."
                />
            );
        case RecodeErrorType.ONE_TIME_EXIT_SCANNED:
            return (
                <Localized
                    de="Einmalausfahrten können nicht rabbattiert werden."
                    fr="Les sorties uniques ne peuvent pas bénéficier d'une rabais."
                    it="Le uscite singole non possono beneficiare di un ribasso."
                    en="One-time exits aren't eligible for a discount."
                />
            );
        case RecodeErrorType.ZONE_NOT_AVAILABLE:
            return (
                <Localized
                    de="Keine Rabattbedingung verfügbar."
                    fr="Aucune condition de rabais disponible."
                    it="Nessuna condizione di ribasso disponibile."
                    en="No discount condition available."
                />
            );
        case RecodeErrorType.ALREADY_RECODED:
            return (
                <Localized
                    de="Auf diese Transaktion wurde schon ein automatischer Rabatt angewendet. Eine Anpassung am Tarif ist nicht möglich."
                    fr="Un rabais automatique a déjà été appliqué à cette transaction. Une modification supplémentaire du tarif n'est pas possible."
                    it="Un ribasso automatico è già stato applicato a questa transazione. Non è possibile apportare ulteriori modifiche alla tariffa."
                    en="An automatic discount has already been applied to this transaction. No further adjustment to the tariff is possible."
                />
            );
        case RecodeErrorType.NOT_REGULAR_CONTEXT:
            return (
                <Localized
                    de="Nur Kurzparker-Transaktionen können rabattiert werden."
                    fr="Seules les transactions horaires publiques peuvent bénéficier d'une rabais."
                    it="Solo les transazioni orarie pubbliche possono beneficiare di un ribasso."
                    en="Only public hourly transactions are eligible for a discount."
                />
            );
        case RecodeErrorType.NO_TRANSACTION_FOUND:
            return (
                <>
                    <p>
                        <Localized
                            de="Für dieses Fahrzeug wurde keine laufende Transaktion gefunden."
                            fr="Aucune transaction en cours n'a été trouvée pour ce véhicule."
                            it="Non è stata trovata alcuna transazione in corso per questo veicolo."
                            en="No ongoing transactions were found for this vehicle."
                        />
                    </p>
                    <p>
                        <Localized
                            de="Sofern anwendbar, wurden auch Transaktionen mit Badge berücksichtigt."
                            fr="Les transactions avec badge ont également été prises en compte, le cas échéant."
                            it="Sono state considerate, dove applicabile, anche le transazioni con badge."
                            en="Transactions with badges were also considered, where applicable."
                        />
                    </p>
                </>
            );
        case RecodeErrorType.MULTIPLE_TRANSACTION_FOUND:
            return (
                <Localized
                    de="Es war nicht möglich, eine einzige Transaktion eindeutig zu bestimmen."
                    fr="Il n'a pas été possible de déterminer de manière univoque une seule transaction."
                    it="Non è stato possibile determinale in modo univoco una sola transazione."
                    en="It was not possible to uniquely determine a single transaction."
                />
            );
        case null:
            return (
                <Localized
                    de="Ein unerwarteter Fehler ist aufgetreten."
                    fr="Une erreur inattendue s'est produite."
                    it="Si è verificato un errore imprevisto."
                    en="An unexpected error has occurred."
                />
            );
    }
}

function RecodeResponseErrorTitle({ type }: { type: RecodeErrorType | null }) {
    switch (type) {
        case RecodeErrorType.NO_TRANSACTION_FOUND:
            return (
                <Localized
                    de="Nicht gefunden"
                    fr="Non trouvée"
                    it="Non trovata"
                    en="Not found"
                />
            );
        case RecodeErrorType.MULTIPLE_TRANSACTION_FOUND:
            return (
                <Localized
                    de="Nicht eindeutig"
                    fr="Non univoque"
                    it="Non univoca"
                    en="Not unique"
                />
            );
    }

    return (
        <Localized
            de="Rabatt fehlgeschlagen"
            fr="Rabais échoué"
            it="Ribasso non riuscito"
            en="Discount failed"
        />
    );
}
